// Shared-session integration follows the shipped Doofinder loader/layer contract.
// See services/search-adapter/README.md for provenance and release verification.
(function () {
  'use strict';
  const config = window.florafyCustomSearch;
  if (
    !window.florafySearchLayerGuard ||
    !config ||
    window.florafySearchAttribution
  )
    return;
  let epoch = 0;
  let searchTimer;
  const confirmedRequests = new WeakMap();
  const cardContexts = new WeakMap();
  const pending = new Set();
  const sent = new Set();
  function allowed() {
    return !!(
      window.Cookiebot?.hasResponse &&
      window.Cookiebot.consent?.statistics &&
      window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()
    );
  }
  const id = () => crypto.randomUUID().replace(/-/g, '');
  const contextKey = () =>
    JSON.stringify([
      config.locale,
      config.country,
      config.currency,
      config.candidateLocationId || '',
    ]);
  function session() {
    const shared = window.Doofinder?.Session;
    if (
      !shared ||
      typeof shared.getSessionId !== 'function' ||
      typeof shared.getUserId !== 'function' ||
      typeof shared.setLive !== 'function'
    )
      return null;
    try {
      const sessionId = shared.getSessionId();
      const userId = shared.getUserId();
      if (
        !/^[a-f0-9]{32}$/.test(sessionId || '') ||
        !/^[a-f0-9-]{1,36}$/.test(userId || '')
      )
        return null;
      return { sessionId, userId };
    } catch {
      return null;
    }
  }
  function prepare() {
    if (!allowed()) return null;
    window.florafyLoadSearchSession?.();
    const current = session();
    return current
      ? {
          ...current,
          requestId: id(),
          consent: true,
          epoch,
          contextKey: contextKey(),
        }
      : null;
  }
  async function ready() {
    const startEpoch = epoch;
    let tracking = prepare();
    for (
      let attempt = 0;
      !tracking && allowed() && epoch === startEpoch && attempt < 20;
      attempt += 1
    ) {
      await new Promise((resolve) => setTimeout(resolve, 40));
      if (epoch !== startEpoch) return null;
      tracking = prepare();
    }
    return tracking;
  }
  function current(tracking) {
    return !!(
      tracking &&
      allowed() &&
      tracking.epoch === epoch &&
      tracking.contextKey === contextKey() &&
      session()?.sessionId === tracking.sessionId &&
      session()?.userId === tracking.userId
    );
  }
  function confirm(tracking, query, returnedSession, engineHash) {
    if (
      !query.trim() ||
      !current(tracking) ||
      returnedSession !== tracking.sessionId
    )
      return false;
    // The native layer uses this exported method after connecting. The custom
    // owner marks the same fixed app only after a real successful query.
    try {
      window.Doofinder.Session.setLive('layer');
      confirmedRequests.set(tracking, { ...tracking });
      search(tracking, query, engineHash);
      return true;
    } catch {
      return false;
    }
  }
  function wire(tracking) {
    if (!current(tracking)) return undefined;
    return {
      sessionId: tracking.sessionId,
      userId: tracking.userId,
      requestId: tracking.requestId,
      consent: true,
    };
  }
  function cancelSearch() {
    clearTimeout(searchTimer);
    searchTimer = undefined;
  }
  function search(tracking, query, engineHash) {
    cancelSearch();
    if (
      !query.trim() ||
      !current(tracking) ||
      !/^[a-f0-9]{32}$/.test(engineHash || '')
    )
      return;
    searchTimer = setTimeout(() => {
      if (current(tracking) && typeof window.gtag === 'function') {
        try {
          window.gtag('event', 'doofinder_search', {
            hashid: engineHash,
            search_term: query,
          });
        } catch {
          /* Optional analytics never changes search behavior. */
        }
      }
    }, 1000);
  }
  function bind(card, tracking) {
    const confirmed = confirmedRequests.get(tracking);
    if (current(confirmed)) cardContexts.set(card, confirmed);
  }
  function capture(card, url) {
    const original = cardContexts.get(card);
    if (
      !current(original) ||
      !card?.dataset.searchAttribution ||
      original.sessionId !== card.dataset.searchSession
    )
      return null;
    return {
      attribution: card.dataset.searchAttribution,
      sessionId: original.sessionId,
      userId: original.userId,
      query: card.dataset.searchQuery,
      contextKey: original.contextKey,
      engineHash: card.dataset.searchEngineHash,
      url,
      epoch: original.epoch,
    };
  }
  function send(action, detail) {
    if (
      detail?.epoch !== epoch ||
      detail.contextKey !== contextKey() ||
      !detail?.attribution ||
      !detail.query ||
      !allowed()
    )
      return;
    const tracking = prepare();
    if (
      !tracking ||
      tracking.sessionId !== detail.sessionId ||
      tracking.userId !== detail.userId
    )
      return;
    const key = action === 'click' ? detail.attribution : detail.eventId;
    if (!key || sent.has(key)) return;
    if (sent.size >= 128 || pending.size >= 4) return;
    sent.add(key);
    if (
      action === 'click' &&
      /^[a-f0-9]{32}$/.test(detail.engineHash || '') &&
      typeof window.gtag === 'function'
    ) {
      try {
        const url = new URL(detail.url);
        if (url.origin === window.location.origin)
          window.gtag('event', 'doofinder_view_item', {
            hashid: detail.engineHash,
            url: url.href,
          });
      } catch {
        /* Invalid or missing canonical navigation does not emit GA. */
      }
    }
    tracking.requestId =
      action === 'cart' ? detail.eventId : tracking.requestId;
    const controller = new AbortController();
    pending.add(controller);
    const timer = setTimeout(() => controller.abort(), 4000);
    fetch(config.path, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        action,
        query: detail.query,
        locale: config.locale,
        country: config.country,
        currency: config.currency,
        ...(config.candidateLocationId
          ? { candidateLocationId: config.candidateLocationId }
          : {}),
        attribution: detail.attribution,
        tracking: wire(tracking),
      }),
    })
      .catch(() => {})
      .finally(() => {
        clearTimeout(timer);
        pending.delete(controller);
      });
    // No navigation delay or automatic retries. A failed response may already
    // have registered upstream, so delivery cannot safely be assumed absent.
  }
  function consentChanged() {
    if (!allowed()) {
      epoch += 1;
      cancelSearch();
      for (const controller of pending) controller.abort();
      pending.clear();
      sent.clear();
      for (const card of document.querySelectorAll?.(
        '[data-search-attribution]',
      ) || []) {
        delete card.dataset.searchAttribution;
        delete card.dataset.searchSession;
        delete card.dataset.searchQuery;
        delete card.dataset.searchEngineHash;
      }
    }
  }
  for (const event of [
    'CookiebotOnConsentReady',
    'CookiebotOnAccept',
    'CookiebotOnDecline',
  ])
    window.addEventListener(event, consentChanged);
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('product-card:added', (event) =>
    send('cart', event.detail),
  );
  window.florafySearchAttribution = {
    prepare,
    ready,
    confirm,
    wire,
    bind,
    capture,
    send,
    cancelSearch,
  };
})();
